/*
 * Zaunfeld-Konfigurator – Berechnungskern (UI-frei, in Node und Browser nutzbar).
 *
 * Exakter Port der Kalkulation aus dem Zaunmaster Cockpit
 * (routers/custom_pricing.py, /api/custom-pricing/calculate):
 *   Latten   = ceil(laenge_m × anzahl_pro_m), Preis je Latte exakt oder
 *              anteilig von der nächstliegenden Höhe skaliert
 *   Riegel   = 2 Reihen; Riegellänge = kleinste Standardlänge ≥ Feldlänge
 *              (über der längsten: halbiert, 2 Stück pro Reihe)
 *   Schrauben= Latten × Riegelreihen × Schrauben/Verbindung × Stückpreis
 *   Arbeit   = Montagepauschale (+ Zuschnittpauschale)
 *   VK       = EK-Summe + max(EK × Aufschlag%, Mindestgewinn)
 *   Versand  = Klassifizierung nach Länge & Gewicht (informativ)
 */
(function (root, factory) {
  if (typeof module !== 'undefined' && module.exports) { module.exports = factory(); }
  else { root.FeldLogic = factory(); }
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  function rund2(x) { return Math.round(x * 100) / 100; }

  function berechneBausatzPreis(kalk, parameter) {
    var teile = rund2(kalk.ekSumme - parameter.montage_pauschale);
    var aufschlag = rund2(teile * (parameter.aufschlag_pct / 100));
    if (aufschlag < parameter.min_gewinn) aufschlag = parameter.min_gewinn;
    var vk = rund2(teile + aufschlag);
    var raster = parameter.preis_raster_eur || 0;
    return raster > 0 ? Math.round(vk / raster) * raster : vk;
  }

  function findeModell(daten, id) {
    for (var i = 0; i < daten.modelle.length; i++) {
      if (daten.modelle[i].id === id) return daten.modelle[i];
    }
    return null;
  }

  function artikelFuer(liste, ausfuehrung) {
    return (liste || []).filter(function (a) { return a.ausfuehrung === ausfuehrung && a.laenge_cm != null; });
  }

  /*
   * Preis für eine Ziellänge: exakter Artikel oder – wie im Cockpit –
   * anteilig von der nächstliegenden Länge skaliert (Preis × ziel/naechste).
   */
  function preisFuerLaenge(artikel, zielCm) {
    var exakt = artikel.filter(function (a) { return a.laenge_cm === zielCm; })[0];
    if (exakt) {
      return { sku: exakt.sku, preis: Number(exakt.preis), gewicht_kg: exakt.gewicht_kg != null ? Number(exakt.gewicht_kg) : null, exakt: true };
    }
    var sortiert = artikel.slice().sort(function (a, b) {
      var da = Math.abs(a.laenge_cm - zielCm) - Math.abs(b.laenge_cm - zielCm);
      return da !== 0 ? da : a.laenge_cm - b.laenge_cm;
    });
    var naechster = sortiert[0];
    if (!naechster) return null;
    return {
      sku: naechster.sku,
      basisLaengeCm: naechster.laenge_cm,
      preis: rund2(Number(naechster.preis) * (zielCm / naechster.laenge_cm)),
      gewicht_kg: null, // Cockpit nutzt bei Skalierung die Gewichts-Heuristik
      exakt: false
    };
  }

  /* Port von classify_shipping() aus dem Cockpit. */
  function versandKlasse(laengeCm, gewichtKg, v) {
    var klassLen = laengeCm;
    if (laengeCm > v.umklassifizieren_ab_cm && gewichtKg < v.umklassifizieren_unter_kg) {
      klassLen = v.ersatz_laenge_cm;
    }
    if (klassLen < v.kurz_grenze_cm) {
      for (var i = 0; i < v.kurz_stufen.length; i++) {
        var stufe = v.kurz_stufen[i];
        if (stufe.max_kg == null || gewichtKg < stufe.max_kg) {
          return { kosten: stufe.kosten, art: stufe.art };
        }
      }
    }
    if (klassLen <= v.mittel_grenze_cm) return { kosten: v.mittel.kosten, art: v.mittel.art };
    return { kosten: v.lang.kosten, art: v.lang.art };
  }

  /* Maximale Feldlänge eines Modells: 2 Riegelstücke pro Reihe müssen reichen. */
  function maxLaengeFuerModell(daten, modellId) {
    var modell = findeModell(daten, modellId);
    if (!modell) return daten.grenzen.max_laenge_m;
    var riegel = artikelFuer(daten.riegel_artikel, modell.riegel_name);
    if (!riegel.length) return daten.grenzen.max_laenge_m;
    var maxRiegel = Math.max.apply(null, riegel.map(function (r) { return r.laenge_cm; }));
    return Math.min(daten.grenzen.max_laenge_m, (2 * maxRiegel) / 100);
  }

  function berechneFeld(eingaben, daten) {
    var fehler = [];
    var g = daten.grenzen;
    var p = daten.parameter;

    var modell = findeModell(daten, eingaben.modellId);
    if (!modell) fehler.push('Bitte ein Zaunmodell wählen.');

    var laengeM = parseFloat(eingaben.laenge_m);
    if (isNaN(laengeM) || laengeM <= 0) {
      fehler.push('Bitte eine Feldlänge angeben.');
    } else if (laengeM < g.min_laenge_m || laengeM > g.max_laenge_m) {
      fehler.push('Die Feldlänge muss zwischen ' + g.min_laenge_m + ' m und ' + g.max_laenge_m + ' m liegen.');
    }

    var hoeheCm = parseInt(eingaben.hoehe_cm, 10);
    if (!hoeheCm) {
      fehler.push('Bitte eine Feldhöhe angeben.');
    } else if (hoeheCm < g.min_hoehe_cm || hoeheCm > g.max_hoehe_cm) {
      fehler.push('Die Feldhöhe muss zwischen ' + g.min_hoehe_cm + ' cm und ' + g.max_hoehe_cm + ' cm liegen.');
    }

    if (modell && laengeM && laengeM > maxLaengeFuerModell(daten, modell.id)) {
      fehler.push('Für ' + modell.name + ' sind maximal ' + String(maxLaengeFuerModell(daten, modell.id)).replace('.', ',') + ' m Feldlänge möglich (Riegellängen).');
    }
    if (fehler.length) return { ok: false, fehler: fehler };

    var laengeCm = laengeM * 100;
    var hinweise = [];

    // --- Latten ---
    var latte = (daten.latten || []).filter(function (l) { return l.name === modell.latten_name; })[0];
    var anzahlProM = latte && latte.anzahl_pro_m ? Number(latte.anzahl_pro_m) : 9.0;
    var lattenAnzahl = Math.ceil(laengeM * anzahlProM);

    var lattenArtikel = artikelFuer(daten.latten_artikel, modell.latten_name);
    var lattenPreis = preisFuerLaenge(lattenArtikel, hoeheCm);
    if (!lattenPreis) return { ok: false, fehler: ['Für ' + modell.name + ' sind keine Latten-Artikel hinterlegt.'] };
    if (!lattenPreis.exakt) {
      hinweise.push('Lattenpreis für ' + hoeheCm + ' cm anteilig aus der ' + lattenPreis.basisLaengeCm + '-cm-Latte berechnet.');
    }
    var lattenGewicht = lattenPreis.gewicht_kg != null ? lattenPreis.gewicht_kg : rund2(0.8 * (hoeheCm / 80));
    var lattenGesamt = rund2(lattenAnzahl * lattenPreis.preis);

    // --- Riegel ---
    var riegelArtikel = artikelFuer(daten.riegel_artikel, modell.riegel_name);
    if (!riegelArtikel.length) return { ok: false, fehler: ['Für ' + modell.name + ' sind keine Riegel-Artikel hinterlegt.'] };
    var riegelLaengen = riegelArtikel.map(function (r) { return r.laenge_cm; })
      .filter(function (v, i, arr) { return arr.indexOf(v) === i; })
      .sort(function (a, b) { return a - b; });
    var maxRiegel = riegelLaengen[riegelLaengen.length - 1];

    var riegelLaenge, proReihe;
    if (laengeCm <= maxRiegel) {
      riegelLaenge = riegelLaengen.filter(function (l) { return l >= laengeCm; })[0] || maxRiegel;
      proReihe = 1;
    } else {
      var halb = laengeCm / 2;
      riegelLaenge = riegelLaengen.filter(function (l) { return l >= halb; })[0] || maxRiegel;
      proReihe = 2;
    }

    var riegelPreis = preisFuerLaenge(riegelArtikel, riegelLaenge);
    var riegelReihen = p.riegel_reihen;
    var riegelGesamtStk = riegelReihen * proReihe;
    var riegelGesamt = rund2(riegelGesamtStk * riegelPreis.preis);
    var verschnittCm = Math.round(riegelLaenge * proReihe - laengeCm);
    if (verschnittCm < 0) {
      hinweise.push('Achtung: Die verfügbaren Riegellängen decken die Feldlänge nicht ab – bitte Konfiguration anfragen.');
    }
    var riegelGewicht = riegelPreis.gewicht_kg != null ? riegelPreis.gewicht_kg : 1.5 * (riegelLaenge / 100);

    // --- Schrauben ---
    var schraubenAnzahl = lattenAnzahl * riegelReihen * p.schrauben_pro_latte_riegel;
    var schraubenGesamt = rund2(schraubenAnzahl * p.schrauben_preis);

    // --- Arbeit ---
    var arbeitGesamt = rund2(p.montage_pauschale + p.zuschnitt_pauschale);

    // --- EK-Summe, Aufschlag, Verkaufspreis ---
    var ekSumme = rund2(lattenGesamt + riegelGesamt + schraubenGesamt + arbeitGesamt);
    var aufschlag = rund2(ekSumme * (p.aufschlag_pct / 100));
    if (aufschlag < p.min_gewinn) aufschlag = p.min_gewinn;
    var verkaufspreis = rund2(ekSumme + aufschlag);

    // Shop-Preis: auf das Preisraster aufgerundet (fuer das Varianten-Raster im Shop)
    var raster = p.preis_raster_eur || 0;
    var shopPreis = raster > 0 ? Math.ceil((verkaufspreis - 0.000001) / raster) * raster : verkaufspreis;

    // --- Gewicht & Versand ---
    var gewichtKg = rund2(
      lattenAnzahl * lattenGewicht +
      riegelGesamtStk * riegelGewicht +
      schraubenAnzahl * p.schrauben_gewicht_kg
    );

    // Über der Langgut-Grenze berechnen Speditionen einen Langgut-Zuschlag –
    // dann gilt unabhängig vom Gewicht der Langgut-Tarif, und im Shop wird
    // das separate Langgut-Konfigurator-Produkt (Versandprofil "Langgut") genutzt.
    var langgutAb = daten.versand.langgut_ab_cm != null ? daten.versand.langgut_ab_cm : 240;
    var langgut = laengeCm > langgutAb;
    var versand;
    if (langgut) {
      versand = { kosten: daten.versand.lang.kosten, art: daten.versand.lang.art };
      hinweise.push('Feldlängen über ' + langgutAb + ' cm versenden Speditionen als Langgut – der Langgut-Zuschlag ist im Versandpreis an der Kasse berücksichtigt.');
    } else {
      versand = versandKlasse(laengeCm, gewichtKg, daten.versand);
    }

    return {
      ok: true,
      modell: modell,
      laenge_m: laengeM,
      hoehe_cm: hoeheCm,
      latten: { sku: lattenPreis.sku, anzahl: lattenAnzahl, einzelpreis: lattenPreis.preis, gesamt: lattenGesamt, exakt: lattenPreis.exakt },
      riegel: {
        sku: riegelPreis.sku, laenge_cm: riegelLaenge, proReihe: proReihe, reihen: riegelReihen,
        anzahl: riegelGesamtStk, einzelpreis: riegelPreis.preis, gesamt: riegelGesamt, verschnittCm: verschnittCm
      },
      schrauben: { anzahl: schraubenAnzahl, gesamt: schraubenGesamt },
      arbeit: { montage: p.montage_pauschale, zuschnitt: p.zuschnitt_pauschale, gesamt: arbeitGesamt },
      ekSumme: ekSumme,
      aufschlag: aufschlag,
      verkaufspreis: verkaufspreis,
      shopPreis: shopPreis,
      preisRaster: raster,
      gewichtKg: gewichtKg,
      versand: versand,
      langgut: langgut,
      hinweise: hinweise
    };
  }

  function zusammenfassung(eingaben, daten) {
    var modell = findeModell(daten, eingaben.modellId);
    var teile = [];
    if (modell) teile.push(modell.name);
    if (eingaben.laenge_m && eingaben.hoehe_cm) {
      teile.push(String(eingaben.laenge_m).replace('.', ',') + ' m × ' + eingaben.hoehe_cm + ' cm');
    }
    teile.push('individuell gefertigt');
    return teile.join(' · ');
  }

  return {
    berechneFeld: berechneFeld,
    berechneBausatzPreis: berechneBausatzPreis,
    maxLaengeFuerModell: maxLaengeFuerModell,
    versandKlasse: versandKlasse,
    zusammenfassung: zusammenfassung
  };
}));
